import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core';
import {
  CaseBattleAnimationEnum,
  ICaseBattle,
} from '../interfaces/case-battles.interface';
import { SessionService } from '@core/services/session.service';
import { ContextMenuService } from '@core/services/context-menu.service';
import { CaseBattlesService } from '../case-battles.service';
import { CasesService } from '@features/cases/services/cases.service';
import { SkinComponent } from '../../../core/components/skin/skin.component';
import { fadeInAnimation } from '@core/utils/animations.util';
import { Skin } from '@core/interfaces/skin.interface';
import { sleepWithId } from '@core/utils/sleep.util';
import { KeyValuePipe } from '@angular/common';

interface RoundSkin extends Skin {
  roundNumber: number; //unique
}

@Component({
  selector: 'app-cb-battle-player-skins',
  imports: [SkinComponent, KeyValuePipe],
  templateUrl: './cb-battle-player-skins.component.html',
  styleUrl: './cb-battle-player-skins.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  animations: [fadeInAnimation],
  host: {
    '[class.mobile-hidden]': 'drops().size === 0',
  },
})
export class CbBattlePlayerSkinsComponent {
  public contextMenuService = inject(ContextMenuService);
  public sessionService = inject(SessionService);
  private caseBattlesService = inject(CaseBattlesService);
  private casesService = inject(CasesService);
  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);

  animation = input.required<keyof typeof CaseBattleAnimationEnum>();
  battle = input.required<ICaseBattle>();
  slot = input.required<number>();

  promiseId = signal<number | null>(null);

  settings = computed(() => {
    return this.caseBattlesService.getBattleSettings(this.battle());
  });

  player = computed(() => {
    return (
      this.battle().participants.find((p) => p.slot === this.slot()) ?? null
    );
  });

  drops = signal<Map<number, RoundSkin>>(new Map());

  isWinner = computed(() => {
    return this.battle()
      .winners.map((player) => player.slot)
      .includes(this.player()?.slot ?? 1);
  });

  showResults = computed(() => {
    return (
      this.animation() === 'finished' || this.animation() === 'battle-results'
    );
  });

  dropEffect = effect(async () => {
    if (this.animation() === 'round-results') {
      const { ROLLING_CLOSER_TIMEOUT } = this.settings();
      const { promise, timeoutId } = await sleepWithId(ROLLING_CLOSER_TIMEOUT);
      this.promiseId.set(timeoutId);
      await promise;
      this.promiseId.set(null);
      this.addLatestDrop();
    }
  });

  constructor() {
    this.destroyRef.onDestroy(() => {
      const promiseId = this.promiseId();
      if (promiseId) {
        clearTimeout(promiseId);
      }
    });
  }

  ngOnInit(): void {
    runInInjectionContext(this.injector, () => {
      if (this.battle().status === 'finished') {
        this.setDrops(this.getDroppedItemsBySlot(this.battle()));
      }
      if (this.battle().status === 'running') {
        this.setDrops(this.getDroppedItemsBySlot(this.battle()).slice(0, -1));
      }
    });
  }

  public async handleCallBots() {
    this.caseBattlesService.callBots(this.battle().uid);
  }

  private getRoundsBySlot(battle: ICaseBattle) {
    return battle.rounds.flatMap((round) =>
      round.drops.filter((user) => user.user.slot === this.slot())
    );
  }

  private getLastDroppedItem(battle: ICaseBattle): RoundSkin | null {
    const rounds = this.getRoundsBySlot(battle);
    const round = rounds.at(-1);

    const currentCase = this.casesService
      .casesList()
      .find((c) => c.link === round?.case);

    if (!currentCase) return null;

    const skin = currentCase.skins.find((c) => c.name === round?.skin);

    if (!skin) return null;

    return { ...skin, roundNumber: rounds.length };
  }

  public addLatestDrop() {
    const skin = this.getLastDroppedItem(this.battle());
    if (skin !== null) {
      this.drops.update((prev) => {
        if (!prev.has(skin.roundNumber)) {
          prev.set(skin.roundNumber, skin);
        }
        return new Map(prev);
      });
    }
  }

  public setDrops(skins: RoundSkin[]) {
    const map = new Map<number, RoundSkin>();
    skins.forEach((skin) => {
      if (!map.has(skin.roundNumber)) {
        map.set(skin.roundNumber, skin);
      }
    });
    this.drops.set(map);
  }

  public getDroppedItemsBySlot(battle: ICaseBattle): RoundSkin[] {
    const rounds = this.getRoundsBySlot(battle);
    const cases = this.casesService.casesList();
    const droppedItems = rounds
      .map((droppedItem, i) => {
        const currentCase = cases.find((c) => c.link === droppedItem.case);
        if (!currentCase) return null;

        const skin: RoundSkin = currentCase.skins.find(
          (c) => c.name === droppedItem.skin
        ) as RoundSkin;

        return skin ? { ...skin, roundNumber: i + 1 } : null;
      })
      .filter((item) => item !== null) as RoundSkin[];

    return droppedItems;
  }

  public resetDrops() {
    this.drops.set(new Map());
  }
}
